import React, {useEffect, useState} from "react";
import styles from './dashboard_graphic.module.css';
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {formatTimestamp} from "../../../components/utils";
import {useTranslation} from "react-i18next";

function CustomToolTip({ active, payload, label }) {
    const formatValue = (value, unit) => {
        // Si es número, formatear con máximo 4 decimales
        if (!isNaN(parseFloat(value)) && isFinite(value)) {
            return `${parseFloat(value).toFixed(Math.min(4, 2))} ${unit || ''}`;
        }
        // Si es booleano (0 o 1), mostrar True/False
        if (value === 0 || value === 1 || value === '0' || value === '1') {
            return parseInt(value) === 0 ? 'False' : 'True';
        }
        // Si es string, mostrar directamente
        return `${value} ${unit || ''}`;
    };

    if (active) {
        return (
            <div style={{backgroundColor: '#FFF', border: '1px solid #000', borderRadius: '6px'}} className={'p-1'}>
                <div>Date: {label}</div>
                {payload &&
                    <div>
                        <div>{payload[0].name}: <b>{formatValue(payload[0].value, payload[0].unit)}</b></div>
                        {payload.length > 1 && <div>{payload[1].name}: <b>{formatValue(payload[1].value, payload[1].unit)}</b></div>}
                        {payload.length > 2 && <div>{payload[2].name}: <b>{formatValue(payload[2].value, payload[2].unit)}</b></div>}
                    </div>
                }
            </div>
        );
    }

    return null;
}

function CustomXAxis({ x, y, payload, frequency }) {
    const [datePart, timePart] = payload.value.split(', ');
    if(!datePart || !timePart) {
        console.error("Invalid payload format:", payload.value);
        return "-";
    }
    const [day, month, year] = datePart.split('/').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);

    const hourString = (hour < 10 ? '0' : '') + hour + ':' + (minute < 10 ? '0' : '') + minute;
    const dateString = (day < 10 ? '0' : '') + day + '/' + (month < 10 ? '0' : '') + month + '/' + year;
    return (
        <text x={x} y={y} dy={16} textAnchor="middle" fill="#666">
            {(frequency === 0 || frequency === 1) ? hourString : dateString}
        </text>
    )
}

function CustomYAxis({ x, y, payload, right, color }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor={right ? "start" : "end"} fill={color}>
                {payload.value.toFixed(2)}
            </text>
        </g>
    )
}

export default function DashboardGraphic({data, graph_name, sampling}) {
    const {t} = useTranslation();
    const [domain, setDomain] = useState([-100, 100]);
    const [chartData, setChartData] = useState([]);
    const [lastTimestamp, setLastTimestamp] = useState("");
    const unit = '°C'; //TODO: CHANGE UNIT

    useEffect(() => {
        const actualData = data?.data;
        if (!actualData || actualData.length === 0) return;
        actualData.forEach(element => element.xAxis = formatTimestamp(element.x));
        let last_timestamp = actualData[actualData.length - 1].x;
        setLastTimestamp(last_timestamp);

        let minValue1 = Math.min(...actualData.filter((x) => x.key_1 !== null).map(item => item.key_1));
        let maxValue1 = Math.max(...actualData.filter((x) => x.key_1 !== null).map(item => item.key_1));

        let minValue2 = Math.min(...actualData.filter((x) => x.key_2 !== null).map(item => item.key_2));
        let maxValue2 = Math.max(...actualData.filter((x) => x.key_2 !== null).map(item => item.key_2));

        let minValue3 = Math.min(...actualData.filter((x) => x.key_3 !== null).map(item => item.key_3));
        let maxValue3 = Math.max(...actualData.filter((x) => x.key_3 !== null).map(item => item.key_3));

        let finalMin = Math.min(minValue1, minValue2, minValue3);
        let finalMax = Math.max(maxValue1, maxValue2, maxValue3);

        let delta = finalMin * 0.1;
        if(delta < 0) finalMin += delta;
        else finalMin -= delta;

        delta = finalMax * 0.1;
        if(delta < 0) finalMax -= delta;
        else finalMax += delta;

        finalMin = parseFloat(finalMin.toFixed(2));
        finalMax = parseFloat(finalMax.toFixed(2));

        setDomain([finalMin,  finalMax]);
        setChartData(actualData);
    }, [data]);

    return (
      <div className={styles.container}>
          <div className={styles.header}>
              <div style={{
                  fontSize: 'large',
                  whiteSpace: 'nowrap'
              }}>{graph_name}</div>
              <div style={{
                  fontSize: '0.6rem',
                  color: '#ccc',
                  whiteSpace: 'nowrap'
              }}>
                  <div>{t('dashboard.lastUpdate')}</div>
                  <div style={{fontSize: '0.6rem'}}>{lastTimestamp}</div>
              </div>
          </div>

          <div className={styles.content}>
              <ResponsiveContainer width={'100%'} height={'100%'}>
                  <LineChart data={chartData} margin={{top: 10, right: 20, left: 20, bottom: 10}}>
                      <XAxis
                          dataKey="xAxis"
                          tick={<CustomXAxis frequency={sampling}/>}
                      />

                      <YAxis
                          yAxisId={`left_${data.pk}`}
                          type={'number'}
                          domain={domain}
                          padding={{bottom: 30}}
                          tick={<CustomYAxis unit={unit} color={'#8884d8'}/>}
                      />
                      {data?.key_3 && <YAxis
                          yAxisId={`left2_${data.pk}`}
                          type={'number'}
                          domain={domain}
                          padding={{bottom: 30}}
                          tick={<CustomYAxis unit={unit} color={'#ff7300'}/>}
                      />}
                      {data?.key_2 && <YAxis
                          yAxisId={`right_${data.pk}`}
                          type={'number'}
                          domain={domain}
                          padding={{bottom: 30}}
                          orientation="right"
                          tick={<CustomYAxis unit={unit} right={true} color={'#82ca9d'}/>}
                      />}
                      <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                      <Tooltip content={<CustomToolTip chartData={chartData}/>} />
                      <Line type="monotone" dataKey="key_1" name={data?.key_1} unit={data?.unit_1} stroke="#8884d8" yAxisId={`left_${data.pk}`}/>
                      {data?.key_2 && <Line type="monotone" dataKey="key_2" name={data?.key_2} unit={data?.unit_2} stroke="#82ca9d" yAxisId={`right_${data.pk}`}/>}
                      {data?.key_3 && <Line type="monotone" dataKey="key_3" name={data?.key_3} unit={data?.unit_3} stroke="#ff7300" yAxisId={`left2_${data.pk}`}/>}
                      <Legend />
                  </LineChart>
              </ResponsiveContainer>
          </div>
      </div>
    );
}
